import type {SxProps, Theme} from '@mui/material'
import {alpha} from '@mui/material'
import {CHART_VIEW_H, CHART_VIEW_W, GRAPH_MAX_HEIGHT} from './chartConstants'

/**
 * Shared card look for chat charts (border + subtle elevation), matching the markdown
 * table. Theme-aware: in dark mode it lifts ~5% over the bubble (matching the table's
 * #1f2030 on the #131425 bubble); in light mode it sinks slightly.
 */
const panelBase = (theme: Theme) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 1,
  p: 1.5,
  bgcolor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.03),
})

/**
 * Card-like panel for the stat tiles (and any non-resizable chart content).
 */
export const chartPanelSx: SxProps<Theme> = (theme: Theme) => ({
  my: 1.5,
  width: '100%',
  ...panelBase(theme),
})

// Default panel width: derived from the max chart height and the viewBox ratio
// (width = height * VIEW_W / VIEW_H) plus the panel's padding + border (12*2 + 1*2),
// so the aspect-locked chart fills the panel with no empty space by default.
const GRAPH_DEFAULT_WIDTH = Math.round((GRAPH_MAX_HEIGHT * CHART_VIEW_W) / CHART_VIEW_H) + 26

/**
 * Resizable panel for the line/bar/pie graphs. The SVG wrapper inside is aspect-locked
 * to the viewBox ratio, so the chart always fills the panel with no letterboxing. The
 * default width comes from GRAPH_MAX_HEIGHT (above); `maxWidth: 100%` lets the panel
 * shrink back down when the window gets smaller and lets the user drag it out to the
 * full bubble width. `useResizeGrip` injects a corner grip and drives the resize
 * itself (width only; the aspect-locked chart area sets the height), so the ratio holds
 * live on every frame -- dragging any direction scales the whole graph with no empty
 * space and no distortion. `position: relative` anchors the injected grip.
 */
// Inferred (not annotated `SxProps<Theme>`) as a theme-callback so it can be composed in an
// sx array, e.g. the pie overrides `width`: sx={[resizableGraphPanelSx, {width}]}.
export const resizableGraphPanelSx = (theme: Theme) => ({
  my: 1.5,
  ...panelBase(theme),
  position: 'relative' as const,
  display: 'flex' as const,
  flexDirection: 'column' as const,
  width: GRAPH_DEFAULT_WIDTH,
  maxWidth: '100%',
  minWidth: 320,
})
